import React, { useCallback, useMemo, useState } from "react";
import cx from "classnames";
import styles from "./TinSelectBlock.module.scss";

import IconTin from "assets/img/iconGrindTin.svg";
import IconCaret from "assets/img/iconCaretDown.svg";
import TinBlock from "./TinBlock";
import { useDispatch } from "react-redux";
import {
  removeLineItem,
  restoreLineItem,
  restoreLineItemAsVariant,
  updateLineItem,
} from "redux/actions/checkout";
import { multiTinLookup } from "../checkoutConstants";
import {
  FREE_MATCHA_TIN_SKU,
  findTinLineItem,
  isMatchaLineItem,
} from "../tinLineItems";

const matchaTinVariantTitles = multiTinLookup.matchatin.variantTitles;
const TIN_OPTIONS_ID = "tin-select-options";

// Stands in for the "No Tin" option in the pending selection, which has no
// variant of its own.
const NO_TIN = -1;

// One wording across both breakpoints — the mocks disagreed with each other
// and with the code.
export const TIN_BLOCK_HEADING = "Your free Grind Coffee Tin";

const TinSelectBlock = ({ cart, className }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // The cart is only committed on the response, so on the click alone nothing
  // in `cart` has moved yet. The tin the customer just picked is held here so
  // the radio answers immediately and the summary catches up behind it; it's
  // cleared once the cart is the source of truth again — which also snaps the
  // selection back if the request failed.
  const [pendingSelection, setPendingSelection] = useState(null);
  const isCommitting = pendingSelection !== null;

  const dispatch = useDispatch();

  const relevantLineItem = findTinLineItem(cart);

  const relevantChild = relevantLineItem?.children.find(
    ({ type }) => type === "rule",
  );

  // Only a matcha product's own tin choice swaps its matcha tin in or out. A
  // coffee product's tin leaves the matcha tin alone.
  const freeMatchaTin =
    relevantLineItem && isMatchaLineItem(relevantLineItem)
      ? cart.products.find((item) => item.variant?.sku === FREE_MATCHA_TIN_SKU)
      : null;

  const tinOptions = useMemo(() => {
    if (!relevantChild || !relevantChild.product || !relevantChild.variant) {
      return null;
    }

    return [
      {
        line_item_id: relevantChild.id,
        product_id: relevantChild.product.id,
        variant_id: relevantChild.variant.id,
        image: relevantChild.variant.image || relevantChild.product.image,
        price: relevantChild.price,
        product_title: relevantChild.product.title,
        variant_title: relevantChild.variant.title,
        selected: relevantChild.removed === false,
      },
      ...(relevantChild.alternatives || []).map((alt) => ({
        line_item_id: relevantChild.id,
        product_id: alt.product_id,
        variant_id: alt.variant_id,
        image: alt.image || relevantChild.product.image,
        price: alt.price,
        product_title: alt.title,
        variant_title: alt.variant_title,
        selected: false,
      })),
    ].sort((a, b) => String(a.variant_id).localeCompare(String(b.variant_id)));
  }, [relevantChild]);

  const selectedVariantId = useMemo(
    () => tinOptions?.find((option) => option.selected) || null,
    [tinOptions],
  );

  const reconcileFreeMatchaTin = useCallback(
    (shouldKeep) => {
      if (!freeMatchaTin) {
        return Promise.resolve();
      }

      if (shouldKeep && freeMatchaTin.removed) {
        return dispatch(restoreLineItem(freeMatchaTin.id));
      }

      if (!shouldKeep && !freeMatchaTin.removed) {
        return dispatch(removeLineItem(freeMatchaTin.id));
      }

      return Promise.resolve();
    },
    [dispatch, freeMatchaTin],
  );

  const onRemove = useCallback(() => {
    if (isCommitting || !relevantChild) {
      return;
    }

    setPendingSelection(NO_TIN);

    dispatch(removeLineItem(relevantChild.id))
      .then(() => reconcileFreeMatchaTin(false))
      .finally(() => setPendingSelection(null));
  }, [dispatch, isCommitting, reconcileFreeMatchaTin, relevantChild]);

  const onSelect = useCallback(
    (tinOption) => {
      if (isCommitting) {
        return;
      }

      setPendingSelection(tinOption.variant_id);

      const keepFreeMatchaTin = matchaTinVariantTitles.includes(
        tinOption.variant_title,
      );

      const updateTin = () => {
        if (!relevantChild.removed) {
          return dispatch(
            updateLineItem(
              relevantChild.id,
              tinOption.product_id,
              tinOption.variant_id,
            ),
          );
        }

        if (tinOption.variant_id === relevantChild.variant.id) {
          return dispatch(restoreLineItem(relevantChild.id));
        }

        // Restore and switch as one step: a cart commit in between would flash
        // the previously selected tin into the summary.
        return dispatch(
          restoreLineItemAsVariant(
            relevantChild.id,
            tinOption.product_id,
            tinOption.variant_id,
          ),
        );
      };

      updateTin()
        .then(() => reconcileFreeMatchaTin(keepFreeMatchaTin))
        .finally(() => setPendingSelection(null));
    },
    [dispatch, isCommitting, reconcileFreeMatchaTin, relevantChild],
  );

  if (!tinOptions || tinOptions.length === 0) {
    return null;
  }

  return (
    <div className={cx(styles.container, className)}>
      <button
        aria-controls={TIN_OPTIONS_ID}
        aria-expanded={isExpanded}
        className={cx(styles.header, "gkit-d-flex gkit-items-center")}
        onClick={() => setIsExpanded(!isExpanded)}
        type="button"
      >
        <img alt="" className="gkit-me-2" src={IconTin} />
        <span className="gkit-my-0 gkit-p-17 gkit-bold gkit-flex-grow gkit-text-start">
          {TIN_BLOCK_HEADING}
        </span>
        <img
          alt=""
          className={cx(isExpanded && styles.rotatedCaret)}
          src={IconCaret}
        />
      </button>
      {isExpanded && (
        <div
          aria-label={TIN_BLOCK_HEADING}
          className={styles.rowContainer}
          id={TIN_OPTIONS_ID}
          role="radiogroup"
        >
          {tinOptions.map((tinOption) => (
            <TinBlock
              isCommitting={isCommitting}
              isSelected={
                isCommitting
                  ? pendingSelection === tinOption.variant_id
                  : tinOption.selected
              }
              key={tinOption.variant_id}
              onSelect={() => onSelect(tinOption)}
              tinOption={tinOption}
            />
          ))}

          <TinBlock
            isCommitting={isCommitting}
            isSelected={
              isCommitting
                ? pendingSelection === NO_TIN
                : selectedVariantId === null
            }
            onSelect={onRemove}
            tinOption={null}
          />
        </div>
      )}
    </div>
  );
};

export default TinSelectBlock;
